/**
 * @name umi 的路由配置
 * @description 只支持 path,component,routes,redirect,wrappers,name,icon 的配置
 * @param path  path 只支持两种占位符配置，第一种是动态参数 :id 的形式，第二种是 * 通配符，通配符只能出现路由字符串的最后。
 * @param component 配置 location 和 path 匹配后用于渲染的 React 组件路径。可以是绝对路径，也可以是相对路径，如果是相对路径，会从 src/pages 开始找起。
 * @param routes 配置子路由，通常在需要为多个路径增加 layout 组件时使用。
 * @param redirect 配置路由跳转
 * @param wrappers 配置路由组件的包装组件，通过包装组件可以为当前的路由组件组合进更多的功能。 比如，可以用于路由级别的权限校验
 * @param name 配置路由的标题，默认读取国际化文件 menu.ts 中 menu.xxxx 的值，如配置 name 为 login，则读取 menu.ts 中 menu.login 的取值作为标题
 * @param icon 配置路由的图标，取值参考 https://ant.design/components/icon-cn， 注意去除风格后缀和大小写，如想要配置图标为 <StepBackwardOutlined /> 则取值应为 stepBackward 或 StepBackward，如想要配置图标为 <UserOutlined /> 则取值应为 user 或者 User
 * @doc https://umijs.org/docs/guides/routes
 */
export default [
  // 1. 认证相关（最基础）
  {
    path: '/login',
    layout: false,
    routes: [
      {
        name: '登录页',
        path: '',
        component: './login/LoginPage',
      },
    ],
  },

  // 2. 欢迎页面（一级路由）
  {
    name: '欢迎页面',
    icon: 'SmileOutlined',
    path: '/welcome',
    component: './Welcome',
  },

  // 2.5. 数据看板（一级路由）
  {
    name: '数据看板',
    icon: 'DashboardOutlined',
    path: '/dashboard',
    component: './dashboard/index',
  },



  // ==================== 核心业务管理区 ====================

  // 3. 组织管理（高频核心功能）
  {
    path: '/organization',
    name: '组织管理',
    icon: 'ApartmentOutlined',  // 直接使用组件名称而不是字符串
    routes: [
      {
        path: '',  // 改为相对路径
        redirect: '/organization/index',
      },
      {
        name: '组织架构',
        icon: 'ApartmentOutlined',
        path: '/organization/index',
        component: './organization/index',
      },
      {
        name: '员工管理',
        icon: 'UserOutlined',
        path: '/organization/employee',
        component: './organization/employee',
      },
      {
        name: '部门管理',
        icon: 'TeamOutlined',
        path: '/organization/dept',
        component: './organization/dept',
      },
      {
        name: '岗位管理',
        icon: 'IdcardOutlined',
        path: '/organization/post',
        component: './organization/post',
      },
      {
        name: '组织配置',
        icon: 'SettingOutlined',
        path: '/organization/config',
        component: './organization/config',
      },
    ],
  },

  // 4. 设备管理（核心业务）
  {
    path: '/device',
    name: '设备管理',
    icon: 'DesktopOutlined',
    routes: [
      {
        path: '',  // 改为相对路径
        redirect: '/device/gateway',
      },
      {
        name: '设备网关',
        icon: 'CloudServerOutlined',
        path: '/device/gateway',
        component: './device/gateway',
      },
      {
        name: '设备终端',
        icon: 'DesktopOutlined',
        path: '/device/terminal',
        component: './device/terminal',
      },
      {
        name: '国标设备',
        icon: 'GlobalOutlined',
        path: '/device/gb-device',
        component: './device/gb-device',
      },
      {
        name: '设备型号',
        icon: 'DatabaseOutlined',
        path: '/device/model',
        component: './device/model',
      },
      {
        name: '设备供应商',
        icon: 'TeamOutlined',
        path: '/device/supplier',
        component: './device/supplier',
      },
    ],
  },

  // 5. 视频融合（核心业务）
  {
    path: '/video',
    name: '视频融合',
    icon: 'PlayCircleOutlined',
    routes: [
      {
        path: '',
        redirect: '/video/preview',
      },
      {
        name: '实时预览',
        icon: 'PlayCircleOutlined',
        path: '/video/preview',
        component: './video/preview',
        // wrappers: ['@/components/RoutePermissionWrapper'],
      },
      {
        name: '录制记录',
        icon: 'HistoryOutlined',
        path: '/video/recording-record',
        component: './video/recording-record',
      },
      {
        name: '视频文件',
        icon: 'FileTextOutlined',
        path: '/video/video-file',
        component: './video/video-file',
      },
    ],
  },

  // 6. 事件管理（日常运营核心）
  {
    path: '/event',
    name: '事件管理',
    icon: 'AlertOutlined',
    routes: [
      {
        path: '',  // 改为相对路径
        redirect: '/event/record',
      },
      {
        name: '事件记录',
        icon: 'DatabaseOutlined',
        path: '/event/record',
        component: './event/record',
      },
      {
        name: '事件工单',
        icon: 'FileTextOutlined',
        path: '/event/order',
        component: './event/order',
      },
      // 工单节点已整合到事件工单页面中，此处路由可保留作为备用或删除
      // {
      //   name: '工单节点',
      //   icon: 'NodeIndexOutlined',
      //   path: '/event/node',
      //   component: './event/node',
      // },
      {
        name: '事件接收者',
        icon: 'BellOutlined',
        path: '/event/receiver',
        component: './event/receiver',
      },
      {
        name: '资源文件',
        icon: 'FileOutlined',
        path: '/event/resource-file',
        component: './event/resource-file',
      },
      {
        name: '事件类型',
        icon: 'TagsOutlined',
        path: '/event/type',
        component: './event/type/index',
      },
    ],
  },



  // ==================== 业务支撑服务区 ====================

  // 8. 套餐管理（业务配置）
  {
    path: '/package',
    name: '套餐管理',
    icon: 'GiftOutlined',
    routes: [
      {
        path: '',
        redirect: '/package/my',
      },
      {
        name: '我的套餐',
        icon: 'UserOutlined',
        path: '/package/my',
        component: './package/my',
      },
      {
        name: '下级套餐',
        icon: 'TeamOutlined',
        path: '/package/sub',
        component: './package/sub',
      },
    ],
  },

  // 9. 用户管理（基础服务）
  {
    path: '/user',
    name: '用户管理',
    icon: 'UserOutlined',
    routes: [
      {
        path: '',
        component: './user/index',
      },
    ],
  },

  // ==================== 系统管理区 ====================

  // 11. 系统管理（后台支撑）
  {
    path: '/system',
    name: '系统管理',
    icon: 'SettingOutlined',
    // 暂时移除权限包装器确保页面可访问
    // wrappers: ['@/components/RoutePermissionWrapper'],
    routes: [
      {
        path: '',
        redirect: './menu',
      },
      {
        name: '菜单管理',
        icon: 'MenuOutlined',
        path: '/system/menu',
        component: './system/menu',
      },
      {
        name: '角色管理',
        icon: 'TeamOutlined',
        path: '/system/role',
        component: './system/role',
      },
      {
        name: '字典管理',
        icon: 'BookOutlined',
        path: '/system/dict',
        component: './system/dict',
      },
      {
        name: '系统配置',
        icon: 'SettingOutlined',
        path: '/system/sys-config',
        component: './system/sys-config',
      },
      {
        name: '无认证接口',
        icon: 'TableOutlined',
        path: '/system/no-auth',
        component: './system/no-auth',
      },
    ],
  },

  // 16. 系统根路径配置
  {
    path: '/',
    redirect: '/welcome',
  },
  {
    path: '*',
    layout: false,
    component: './404',
  },
];
